import type { ReactNode } from 'react'

import { type ThemeInterface } from '@procurify/ui-theme'
import { rem, shade, tint } from 'polished'
import styled from 'styled-components'

import { AuditLogRecordLabelStyled } from '@webapp/platform/audit-log/components/AuditLogRecordLabel/styles/AuditLogRecordLabelStyled'
import { type AuditLogStatusEnum } from '@webapp/platform/audit-log/entities/enums/AuditLogStatusEnum'

interface IAuditLogRecordStyledProps {
  children?: ReactNode
  status: AuditLogStatusEnum
}

export const getColorByStatus = (props: {
  theme: ThemeInterface
  status: AuditLogStatusEnum
}): { background: string; content: string } => {
  switch (props.status) {
    case 'Closed':
      return {
        background: props.theme.colors.darkGreen,
        content: props.theme.colors.text.inverse,
      }
    case 'Edited':
      return {
        background: tint(0.7, props.theme.colors.blue),
        content: props.theme.colors.text.link,
      }
    case 'Received':
    case 'Cancelled':
      return {
        background: tint(0.7, props.theme.colors.lightGrey),
        content: shade(0.8, props.theme.colors.lightGrey),
      }
    case 'Approved':
    case 'Purchased':
    case 'Paid':
      return {
        background: tint(0.7, props.theme.colors.positive),
        content: shade(0.3, props.theme.colors.positive),
      }

    case 'Send PO':
    case 'Denied':
    case 'Deleted':
    case 'Rejected':
      return {
        background: tint(0.7, props.theme.colors.red),
        content: shade(0.3, props.theme.colors.red),
      }

    case 'Created':
    case 'Reopened':
      return {
        background: tint(0.7, props.theme.colors.yellow),
        content: shade(0.3, props.theme.colors.yellow),
      }

    case 'Removed':
    default:
      return {
        background: tint(0.7, props.theme.colors.white),
        content: props.theme.colors.text.primary,
      }
  }
}

export const AuditLogRecordStyled = styled.li<IAuditLogRecordStyledProps>`
  position: relative;
  list-style: none;
  background-color: ${(props) => props.theme.colors.content.contentBackground};
  border: 1px solid ${(props) => props.theme.colors.content.border};
  border-left: 3px solid ${(props) => getColorByStatus(props).background};
  padding: ${(props) => {
    const { pxToRem } = props.theme.typography
    return `${pxToRem(26)} ${pxToRem(16)} ${pxToRem(16)}`
  }};
  border-radius: ${(props) => props.theme.typography.pxToRem(4)};

  ${AuditLogRecordLabelStyled} {
    position: absolute;
    top: ${rem(-10)};
    left: ${rem(-3)};
    background-color: ${(props) => getColorByStatus(props).background};
    color: ${(props) => getColorByStatus(props).content};
  }
`
